import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, Mail, ArrowLeft, KeyRound } from 'lucide-react';
import { useAuth } from '../../App';
import { login as apiLogin, requestPasswordReset, resetPassword as apiResetPassword } from '../../api/auth';

const AdminLogin = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [showResetForm, setShowResetForm] = useState(false);
  const [showNewPasswordForm, setShowNewPasswordForm] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [debugToken, setDebugToken] = useState('');
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const data = await apiLogin(email, password);
      login(data.token);
      navigate('/admin/dashboard');
    } catch (err) {
      setError('Nieprawidłowe dane logowania');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestReset = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const data = await requestPasswordReset(resetEmail);
      setSuccess('Jeśli email istnieje w systemie, link resetujący został wysłany.');
      if (data.debug_token) {
        setDebugToken(data.debug_token);
        setShowNewPasswordForm(true);
      }
    } catch (err) {
      setError('Błąd podczas wysyłania żądania');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await apiResetPassword(resetToken || debugToken, newPassword);
      setSuccess('Hasło zostało zmienione! Możesz się teraz zalogować.');
      setShowResetForm(false);
      setShowNewPasswordForm(false);
      setDebugToken('');
      setResetToken('');
      setNewPassword('');
    } catch (err) {
      setError(err.response?.data?.detail || 'Błąd podczas resetowania hasła');
    } finally {
      setLoading(false);
    }
  };

  // Reset Password Request Form
  if (showResetForm && !showNewPasswordForm) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center p-4">
        <div className="glass-card p-8 w-full max-w-md">
          <button 
            onClick={() => { setShowResetForm(false); setError(''); setSuccess(''); }}
            className="flex items-center gap-2 text-zinc-400 hover:text-white mb-6"
          >
            <ArrowLeft size={18} />
            Powrót do logowania
          </button>

          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-orange-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <KeyRound className="w-8 h-8 text-orange-400" />
            </div>
            <h1 className="text-2xl font-bold text-white">Resetuj hasło</h1>
            <p className="text-[#9CA3AF] text-sm mt-1">Podaj email powiązany z kontem admina</p>
          </div>

          <form onSubmit={handleRequestReset} className="space-y-4">
            <div>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B7280]" size={18} />
                <input 
                  type="email" 
                  value={resetEmail} 
                  onChange={(e) => setResetEmail(e.target.value)} 
                  placeholder="admin@mrminds.pl" 
                  className="w-full pl-10" 
                  required 
                  data-testid="reset-email"
                />
              </div>
            </div>

            {error && <p className="text-red-400 text-sm">{error}</p>}
            {success && <p className="text-emerald-400 text-sm">{success}</p>}

            <button type="submit" disabled={loading} className="btn-primary w-full" data-testid="request-reset-btn">
              {loading ? <div className="spinner"></div> : 'Wyślij link resetujący'}
            </button>
          </form>

          <div className="mt-6 p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
            <p className="text-yellow-400 text-sm">
              <strong>Tryb testowy:</strong> Email nie zostanie wysłany. Token do resetu pojawi się po wysłaniu żądania.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // New Password Form (after receiving token)
  if (showNewPasswordForm) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center p-4">
        <div className="glass-card p-8 w-full max-w-md">
          <button 
            onClick={() => { setShowNewPasswordForm(false); setShowResetForm(true); setError(''); setSuccess(''); }}
            className="flex items-center gap-2 text-zinc-400 hover:text-white mb-6"
          >
            <ArrowLeft size={18} />
            Powrót
          </button>

          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-emerald-400" />
            </div>
            <h1 className="text-2xl font-bold text-white">Ustaw nowe hasło</h1>
            <p className="text-[#9CA3AF] text-sm mt-1">Wprowadź nowe hasło dla konta</p>
          </div>

          <form onSubmit={handleResetPassword} className="space-y-4">
            {debugToken && (
              <div className="p-3 bg-zinc-800 rounded-lg">
                <p className="text-xs text-zinc-400 mb-1">Token (tryb testowy):</p>
                <code className="text-xs text-emerald-400 break-all">{debugToken}</code>
              </div>
            )}

            <div>
              <label className="block text-sm text-[#9CA3AF] mb-2">Token resetu (jeśli z emaila)</label>
              <input 
                type="text" 
                value={resetToken} 
                onChange={(e) => setResetToken(e.target.value)} 
                placeholder="Wklej token z emaila..."
                className="w-full" 
                data-testid="reset-token"
              />
            </div>

            <div>
              <label className="block text-sm text-[#9CA3AF] mb-2">Nowe hasło *</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B7280]" size={18} />
                <input 
                  type="password" 
                  value={newPassword} 
                  onChange={(e) => setNewPassword(e.target.value)} 
                  placeholder="••••••••" 
                  className="w-full pl-10" 
                  required 
                  minLength={6}
                  data-testid="new-password"
                />
              </div>
            </div>

            {error && <p className="text-red-400 text-sm">{error}</p>}
            {success && <p className="text-emerald-400 text-sm">{success}</p>}

            <button type="submit" disabled={loading} className="btn-primary w-full" data-testid="reset-password-btn">
              {loading ? <div className="spinner"></div> : 'Zmień hasło'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  // Main Login Form
  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center p-4">
      <div className="glass-card p-8 w-full max-w-md" data-testid="admin-login-form">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-[#10b981]/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <Lock className="w-8 h-8 text-[#10b981]" />
          </div>
          <h1 className="text-2xl font-bold text-white">Panel Admina</h1>
          <p className="text-[#9CA3AF] text-sm mt-1">Zaloguj się, aby zarządzać sklepem</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B7280]" size={18} />
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="email" className="w-full pl-10" required data-testid="login-email" />
            </div>
          </div>

          <div>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B7280]" size={18} />
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="hasło" className="w-full pl-10" required data-testid="login-password" />
            </div>
          </div>

          {error && <p className="text-red-400 text-sm">{error}</p>}
          {success && <p className="text-emerald-400 text-sm">{success}</p>}

          <button type="submit" disabled={loading} className="btn-primary w-full" data-testid="login-submit">
            {loading ? <div className="spinner"></div> : 'Zaloguj się'}
          </button>
        </form>

        <button 
          onClick={() => setShowResetForm(true)}
          className="w-full text-center text-[#9CA3AF] hover:text-[#10b981] text-sm mt-4 transition-colors"
          data-testid="forgot-password-btn"
        >
          Zapomniałem hasła
        </button>
      </div>
    </div>
  );
};

export default AdminLogin;